<script setup lang="ts">
// Viewpart for the `workspace_corpus_search` tool: renders its workspace
// message hits as a compact card list (channel + author + date + text).
// Registered in lib/toolRenderers.ts; rendered by ToolViewparts below the
// agent's prose, collapsed by default via the shared Fold so it doesn't pollute
// the thread.
//
// Reads tool.outputFull - shape `{ hits: [{channel_name, author, posted_at,
// text, ...}] }` (CorpusSearchOutput / CorpusHit). These are workspace
// conversation messages, read-only context - no link. text renders as TEXT
// (Vue escapes - no HTML injection).
import { computed } from 'vue';
import Fold from '../Fold.vue';
import { useIcons } from '../../icons';
import { formatDate } from '../../lib/datetime';
import type { ToolStep } from '../../types';

const props = defineProps<{ tool: ToolStep; base?: number }>();
const icons = useIcons();

interface Hit { channel: string; author: string; when: string; text: string; }

function fmtDate(s: string): string {
  return formatDate(s, undefined, '');
}

const hits = computed<Hit[]>(() => {
  const raw = props.tool.outputFull;
  if (!raw) return [];
  let data: unknown;
  try { data = JSON.parse(raw); } catch { return []; }
  const arr: unknown = Array.isArray(data)
    ? data
    : (data && typeof data === 'object' && Array.isArray((data as { hits?: unknown }).hits))
      ? (data as { hits: unknown[] }).hits
      : [];
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((h): h is Record<string, unknown> => !!h && typeof h === 'object' && typeof h.text === 'string')
    .map((h) => ({
      channel: String(h.channel_name ?? ''),
      author:  String(h.author ?? ''),
      when:    fmtDate(String(h.posted_at ?? '')),
      text:    String(h.text ?? ''),
    }))
    .filter((h) => h.text.trim() !== '');
});
</script>

<template>
  <Fold v-if="hits.length">
    <template #summary>
      <component :is="icons.chat" :size="13" class="ws__icon" />
      <span>From your workspace</span>
      <span class="ws__count">{{ hits.length }}</span>
    </template>
    <ul class="ws__list" role="list">
      <li v-for="(h, i) in hits" :key="i" class="ws__item" :data-cite="(base ?? 0) + i + 1">
        <div v-if="h.channel || h.author || h.when" class="ws__meta">
          <span v-if="h.channel" class="ws__channel">#{{ h.channel }}</span>
          <span v-if="h.author" class="ws__author">{{ h.author }}</span>
          <span v-if="h.when" class="ws__when">{{ h.when }}</span>
        </div>
        <p class="ws__text">{{ h.text }}</p>
      </li>
    </ul>
  </Fold>
</template>

<style scoped>
.ws__icon { flex: 0 0 auto; }
.ws__count { color: var(--ink-subtle); }

.ws__list {
  margin: var(--space-2) 0 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md, 10px);
  background: var(--surface-panel);
  overflow: hidden;
}
.ws__item { padding: var(--space-2) var(--space-3); }
.ws__item + .ws__item { border-top: 1px solid var(--border-subtle); }
.ws__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-1) var(--space-2);
  font-size: var(--text-xs);
}
.ws__channel { font-weight: 600; color: var(--ink-display); }
.ws__author { color: var(--ink-muted); }
.ws__when { color: var(--ink-subtle); }
.ws__text {
  margin: var(--space-1) 0 0;
  font-size: var(--text-xs);
  line-height: 1.5;
  color: var(--ink-muted);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
